export const voiceTranscribeDocumentationMarkdown = `
### What does this block do?

The Voice Transcribe Block takes a voice recording file stored in cloud storage and converts its audio into text. Supporting various formats (mp3, mp4, wav, flac, ogg, amr, webm, m4a), it offers two transcription modes—Dictation and Conversation—to best suit the type of audio being processed. This capability enables workflows to process spoken language data (like telehealth recordings, dictations, or patient messages) into searchable and analyzable text.

### When to use it?

- Use this block when you need to convert audio recordings into text for documentation, analysis, or integration into other systems like EHRs.  
- Employ it in workflows that involve clinical dictations, telehealth sessions, or patient voicemails.  
- Integrate it when a written representation of a conversation or a dictation is essential for downstream processing.

### Example Flows

1. **Telehealth Session Documentation:**  
    After a telehealth appointment, the recorded conversation is stored in cloud storage. The Voice Transcribe Block converts this recording into text, which is then attached to the patient's record for clinical review and record-keeping.

2. **Patient Voicemail Processing:**  
    Patients may leave voicemails regarding appointment scheduling or follow-ups. This block transcribes such messages into text, allowing staff to quickly assess and act on the content.

3. **Clinical Dictation Capture:**  
    Clinicians record their notes verbally following patient consultations. The block transcribes these dictations, converting the speech into text that can be used for timely updates to the patient's electronic health record.

## How to Configure This Block

### **1. Cloud Storage Path**  
- **Description:** Enter the full cloud storage path to the voice recording file.  
- **Helper Text:** "Required - Cloud storage path to the voice recording file."  
- **Note:** Ensure that the file is in one of the supported formats: mp3, mp4, wav, flac, ogg, amr, webm, or m4a.

### **2. Transcription Type**  
- **Description:** Select the transcription mode from the "Transcription Type" dropdown.  
- **Options:**  
  - **Dictation:** For long, continuous speeches.  
  - **Conversation:** For interactive dialogues (default selection).  

### **3. Mock Output for Test Run**  
- **Description:** Toggle the "Mock output for test run" switch (default is Off).  
- **Helper Text:** "When enabled, this block will skip running during a test run and output the data below."  
- **Use Case:** Simulate transcriptions without processing live audio.

### **4. Save Your Configuration**  
Once all fields are configured, click Save to update the block settings in the workflow.

## How to Test

### **1. Prepare a Test Audio File**  
- Upload a voice recording file (in one of the supported formats) to your designated cloud storage.

### **2. Enable Test Mode (Optional)**  
- Toggle "Mock output for test run" to On to simulate the transcription process.

### **3. Run the Flow**  
- Execute the flow and verify that the block uses the provided Cloud Storage Path to retrieve the audio file and transcribes it according to the selected Transcription Type.

### **4. Review the Transcription**  
- Confirm that the output contains accurate, well-formatted text.

### **5. Validate Error Handling**  
- Test the block with an incorrect file path or an unsupported file format to ensure it fails gracefully and returns a useful error message.

## Best Practices

1. **Ensure File Accessibility:**  
    Verify that the cloud storage path is correctly entered and that the file is accessible.

2. **Confirm File Format:**  
    Double-check the audio file is in one of the supported formats to avoid transcription errors.

3. **Select the Appropriate Transcription Mode:**  
    Choose "Dictation" for long, continuous speeches or "Conversation" for interactive dialogues.

4. **Leverage Test Mode:**  
    Use the "Mock output for test run" feature during development to verify the workflow without processing live files.

5. **Review and Adjust:**  
    After processing a live file, review the transcribed text for accuracy and modify audio settings or parameters if necessary.

6. **Integrate Smoothly:**  
    Ensure the output format of the transcribed text is compatible with downstream blocks in your workflow.
`;
